import { useEffect, useState } from "react";
import { X, Send } from "lucide-react";
import { useNavigate } from "react-router-dom";
import api, { timeAgo } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Avatar } from "./Avatar";

export const CommentsDrawer = ({ open, onClose, video, onCountChange }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [comments, setComments] = useState([]);
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(false);

  const isArtist = user && (user.is_artist || user.role === "admin");

  useEffect(() => {
    if (open && video) {
      setLoading(true);
      api.get(`/videos/${video.id}/comments`).then((r) => setComments(r.data)).finally(() => setLoading(false));
    }
  }, [open, video]);

  if (!open || !video) return null;

  const submit = async (e) => {
    e.preventDefault();
    if (!user) { navigate("/login"); return; }
    if (!text.trim()) return;
    const { data } = await api.post(`/videos/${video.id}/comments`, { text: text.trim() });
    setComments((c) => [data, ...c]);
    setText("");
    onCountChange && onCountChange(video.id, (video.comment_count || 0) + 1);
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-end justify-center" data-testid="comments-drawer">
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />
      <div className="relative w-full max-w-lg bg-zinc-950 border-t border-white/10 rounded-t-2xl max-h-[72vh] flex flex-col nx-fade-up">
        <div className="flex items-center justify-between px-5 py-4 border-b border-white/10">
          <h3 className="font-display font-bold text-white">{comments.length} Comments</h3>
          <button onClick={onClose} data-testid="close-comments" className="text-zinc-400 hover:text-white"><X className="h-5 w-5" /></button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-5">
          {loading ? (
            <p className="text-zinc-500 text-sm">Loading…</p>
          ) : comments.length === 0 ? (
            <p className="text-zinc-500 text-sm py-8 text-center">No comments yet.</p>
          ) : comments.map((c) => (
            <div key={c.id} className="flex gap-3" data-testid={`comment-${c.id}`}>
              <Avatar src={c.user_avatar} name={c.user_name} className="h-9 w-9 rounded-full border border-white/10 flex-shrink-0" textClass="text-sm" />
              <div>
                <p className="text-sm"><span className="font-semibold text-white">{c.user_name}</span> <span className="text-zinc-500 text-xs">· {timeAgo(c.created_at)}</span></p>
                <p className="text-zinc-300 text-sm mt-0.5">{c.text}</p>
              </div>
            </div>
          ))}
        </div>

        {user ? (
          <form onSubmit={submit} className="flex gap-2 p-4 border-t border-white/10">
            <input
              data-testid="comment-input"
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="Add a comment…"
              className="flex-1 bg-zinc-900 border border-white/10 text-white placeholder-zinc-500 px-4 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-[#FFD60A]"
            />
            <button type="submit" data-testid="comment-submit" className="bg-[#FFD60A] text-black px-4 hover:bg-[#FFD60A]/90 transition-colors"><Send className="h-4 w-4" /></button>
          </form>
        ) : (
          <div className="p-4 border-t border-white/10 text-center text-xs text-zinc-500">
            Log in to join the conversation.
          </div>
        )}
      </div>
    </div>
  );
};
